import React, { useCallback } from 'react';

import { formatAccountNumber, sanitizeAccountNumber } from '@eika-infrastruktur/format';
import { AttentionMessage } from '@eika/attention-message';
import { Loader } from '@eika/loader';

import { Account, AccountNumber } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { AccountLabels } from '../../../shared/AccountLabels';
import { currencyFormatter } from '../../../shared/numberFormat';
import { isUnknownAccount } from '../../../transaction/Transfer/hooks/useToAccount';

import './FromAccountSelect.scss';

interface Props {
    selected?: AccountNumber;
    id: string;
    selectListRef: React.MutableRefObject<HTMLUListElement | null>;
    isLoading: boolean;
    itemsWithoutSelected: Account[];
    selectItem: (accountNumber: AccountNumber) => void;
    listItemKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>, account: AccountNumber) => void;
    showNegativeAmount: boolean;
    items: Account[];
}

const ValidAccountsList = ({
    selected,
    id,
    selectListRef,
    listItemKeyDown,
    isLoading,
    selectItem,
    itemsWithoutSelected,
    showNegativeAmount,
    items,
}: Props) => {
    const isSelected = useCallback(
        (accountNumber: AccountNumber): boolean => {
            if (selected) {
                return sanitizeAccountNumber(selected.value) === accountNumber.value;
            }
            return false;
        },
        [selected],
    );
    const { onlyOneAccount } = useTranslation();

    return (
        <ul
            className="from-account-select__list"
            role="listbox"
            aria-activedescendant={selected ? `selected-${id}-${sanitizeAccountNumber(selected.value)}` : undefined}
            tabIndex={-1}
            aria-labelledby={`combo-label-${id}`}
            ref={selectListRef}
        >
            {isLoading && <Loader mini />}
            {!isLoading &&
                itemsWithoutSelected.length > 0 &&
                itemsWithoutSelected.map((item) => (
                    <li
                        key={id + item.accountId.value}
                        id={`selected-${id}-${sanitizeAccountNumber(item.accountNumber.value)}`}
                        role="option"
                        className="from-account-select__list-item"
                        aria-selected={isSelected(item.accountNumber)}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                selectItem(item.accountNumber);
                            }}
                            className="from-account-select__list-item-button"
                            onKeyDown={(e) => listItemKeyDown(e, item.accountNumber)}
                        >
                            <span className="from-account-select__list-item-owners">
                                <AccountLabels owners={item.owners} className="account-button__owner-label inactive" />
                            </span>
                            <span className="from-account-select__list-item-left-text">{item.alias}</span>
                            <span
                                className="from-account-select__list-item-left-sub-text"
                                data-testid="from-account-numbers"
                            >
                                {formatAccountNumber(item.accountNumber)}
                            </span>
                            <span
                                className="from-account-select__list-item-right-text"
                                data-testid="from-account-balances"
                            >
                                {!isUnknownAccount(item) &&
                                    currencyFormatter(
                                        showNegativeAmount
                                            ? item.balance.accountingBalance
                                            : item.balance.availableBalance,
                                        item.currency,
                                    )}
                            </span>
                        </button>
                    </li>
                ))}
            {!isLoading && items.length === 1 && itemsWithoutSelected.length === 0 && (
                <li className="from-account-select__list-item is-empty">
                    <span className="from-account-select__list-item-button">
                        <span className="from-account-select__list-item-left-text empty-text">
                            <AttentionMessage type="info" text={onlyOneAccount} />
                        </span>
                    </span>
                </li>
            )}
        </ul>
    );
};

export default ValidAccountsList;
